import { Component, input, output, model, computed, signal, ChangeDetectionStrategy, ElementRef, HostListener, inject } from '@angular/core';
import { FormStatus } from '@formrails/form-builder/data-access';
import { PromotionDto } from '@formrails/shared/data-access';
import { FormType, FORM_TYPE_OPTIONS } from '@formrails/shared/ui';

/**
 * Toolbar for the form builder, grouped by purpose:
 * - left: working with forms (new, open, compare), the question-numbers
 *   toggle and a menu for occasional or destructive actions (clear, archive);
 * - right: this form's status and its lifecycle (save draft, publish).
 *
 * Workflow: Draft forms can be saved as draft or published; published forms
 * are live.
 */
@Component({
  selector: 'app-form-toolbar',
  standalone: true,
  templateUrl: './form-toolbar.component.html',
  styleUrl: './form-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormToolbarComponent {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  // Two-way binding with model signal for the numbering toggle
  readonly showQuestionNumbers = model(true);

  // Signal inputs
  readonly formStatus = input<FormStatus>(FormStatus.Draft);
  readonly isReadOnly = input(false);
  readonly hasFormElements = input(false);
  /** Edits not yet saved; shown beside the status. */
  readonly hasUnsavedChanges = input(false);
  /** How far this version has travelled towards the next environment. */
  readonly promotion = input<PromotionDto | null>(null);
  /**
   * Where a promotion from here lands — sandbox from CI, production from
   * sandbox. Null in production, where there is nowhere further to go.
   */
  readonly promotionTarget = input<string | null>(null);

  // Signal outputs
  readonly showQuestionNumbersChange = output<boolean>();
  readonly clearForm = output<void>();
  readonly openSavedForms = output<void>();
  /** Pick a saved form to show beside this one. */
  readonly compare = output<void>();
  /** The chosen form type, picked from the menu under New. */
  readonly createForm = output<FormType>();
  readonly saveDraft = output<void>();
  readonly publish = output<void>();
  /** Ask for this version to be promoted to the next environment. */
  readonly promote = output<void>();
  /** Record that the client has signed the promotion off. */
  readonly approvePromotion = output<void>();
  readonly archive = output<void>();

  readonly FormStatus = FormStatus;

  /** The ⋯ menu (clear, archive). */
  readonly moreMenuOpen = signal(false);

  /** The form-type menu under New; two-way so the welcome dialog can open it. */
  readonly typeMenuOpen = model(false);
  readonly formTypes = FORM_TYPE_OPTIONS;

  readonly canArchive = computed(() => this.formStatus() !== FormStatus.Archived);

  /**
   * Promotion is offered on a published version: what the client signs off is
   * what they can already see. Production has nowhere to promote to, so the
   * action is absent there rather than disabled.
   */
  readonly canPromote = computed(() =>
    this.formStatus() === FormStatus.Published && !!this.promotionTarget()
  );

  /**
   * Where this version is headed, in prose. The environment recorded on the
   * promotion wins once there is one: it says where the version was actually
   * sent, which need not be where this environment sends things today.
   */
  readonly targetLabel = computed(() =>
    (this.promotion()?.targetEnvironment ?? this.promotionTarget() ?? 'production').toLowerCase()
  );

  /**
   * The one thing left to do, which depends on how far the promotion has got:
   * ask for it, sign it off, or wait while the worker does its part.
   */
  readonly promoteLabel = computed(() => {
    switch (this.promotion()?.state) {
      case 'Requested': return 'Mark approved';
      case 'Approved': return 'Promoting';
      case 'Promoted': return `In ${this.targetLabel()}`;
      case 'Failed': return 'Retry';
      default: return 'Promote';
    }
  });

  readonly promoteIcon = computed(() => {
    switch (this.promotion()?.state) {
      case 'Requested': return 'pi pi-check';
      case 'Approved': return 'pi pi-spin pi-spinner';
      case 'Promoted': return 'pi pi-check-circle';
      case 'Failed': return 'pi pi-refresh';
      default: return 'pi pi-send';
    }
  });

  /** Only the worker can move it on from here. */
  readonly promoteDisabled = computed(() => {
    const state = this.promotion()?.state;
    return this.isReadOnly() || this.hasUnsavedChanges() ||
      state === 'Approved' || state === 'Promoted';
  });

  readonly promoteTitle = computed(() => {
    if (this.hasUnsavedChanges()) {
      return 'Save your changes before promoting';
    }
    switch (this.promotion()?.state) {
      case 'Requested': return 'Record that the client has approved this version';
      case 'Approved': return `Signed off and waiting to be copied to ${this.targetLabel()}`;
      case 'Promoted': return `This version is already in ${this.targetLabel()}`;
      case 'Failed': return 'The last attempt failed — send it again';
      default: return `Ask for this version to be promoted to ${this.targetLabel()}`;
    }
  });

  /**
   * Requested and Failed both need the sign-off recorded; anything else is a
   * fresh request.
   */
  onPromotionAction(): void {
    const state = this.promotion()?.state;
    if (state === 'Requested' || state === 'Failed') {
      this.approvePromotion.emit();
    } else {
      this.promote.emit();
    }
  }

  toggleQuestionNumbers(): void {
    const value = !this.showQuestionNumbers();
    this.showQuestionNumbers.set(value);
    this.showQuestionNumbersChange.emit(value);
  }

  toggleMoreMenu(): void {
    this.typeMenuOpen.set(false);
    this.moreMenuOpen.update(open => !open);
  }

  toggleTypeMenu(): void {
    this.moreMenuOpen.set(false);
    this.typeMenuOpen.update(open => !open);
  }

  selectFormType(formType: FormType): void {
    this.typeMenuOpen.set(false);
    this.createForm.emit(formType);
  }

  onClearClick(): void {
    this.moreMenuOpen.set(false);
    this.clearForm.emit();
  }

  onArchiveClick(): void {
    this.moreMenuOpen.set(false);
    this.archive.emit();
  }

  /**
   * Close an open menu on a click anywhere outside it. The checks name the
   * menu and the button that opens it, not their container: the type menu
   * hangs off the New/Open/Compare group, so treating the whole group as
   * "inside" would leave it open when Open or Compare is clicked.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const clickedInside = (selector: string): boolean => !!target?.closest?.(selector);
    if (this.moreMenuOpen() && !clickedInside('.more-menu, .more-menu-trigger')) {
      this.moreMenuOpen.set(false);
    }
    if (this.typeMenuOpen() && !clickedInside('.type-menu, .type-menu-trigger')) {
      this.typeMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.moreMenuOpen.set(false);
    this.typeMenuOpen.set(false);
  }
}
